/**
 * Deposit Mutations — เงินมัดจำรายการเช่า (Deposit v2 Phase 1)
 *
 * ทุก hook คืน DepositActionResponse { item, rental_deposit_summary, warnings } ผ่าน mutateAsync
 * - success toast ยิงจากที่นี่ · warnings[] ให้ caller แสดงเอง (ไม่บล็อค)
 * - error ไม่ toast จากที่นี่ — modal/หน้าเรียกต้องแสดง error.user_message||message เอง
 *   (กัน toast ซ้ำกับ inline error ใน modal — pattern เดียวกับ BUG-FE-TOAST-DUP)
 * - invalidate: ['rentals'] (list/queryKeys.rentals.all), ['rental', id] (detail consumers),
 *   ['rental-activities', id] (timeline) — RentalViewPage โหลดด้วย useState เอง จึง reload ใน onSuccess ของหน้า
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import depositService, {
  DepositActionResponse,
  DepositHoldRequest,
  DepositMarkPaidRequest,
  DepositReopenRequest,
  DepositReturnRequest,
  DepositUnholdRequest,
} from '../../services/depositService';
import { showSuccess } from '../../utils/toast';

interface DepositVars<TBody> {
  rentalId: number;
  itemId: number;
  data: TBody;
}

const useInvalidateDeposit = () => {
  const queryClient = useQueryClient();
  return (rentalId: number) => {
    queryClient.invalidateQueries({ queryKey: ['rentals'] });
    queryClient.invalidateQueries({ queryKey: ['rental', rentalId] });
    queryClient.invalidateQueries({ queryKey: ['rental-activities', rentalId] });
  };
};

/** บันทึกคืนมัดจำ → RETURNED */
export const useReturnDeposit = () => {
  const invalidate = useInvalidateDeposit();
  return useMutation<DepositActionResponse, Error, DepositVars<DepositReturnRequest>>({
    mutationFn: ({ rentalId, itemId, data }) => depositService.returnDeposit(rentalId, itemId, data),
    onSuccess: (_res, { rentalId }) => {
      invalidate(rentalId);
      showSuccess('บันทึกการคืนมัดจำเรียบร้อยแล้ว');
    },
  });
};

/** คงมัดจำไว้ก่อน → hold=true */
export const useHoldDeposit = () => {
  const invalidate = useInvalidateDeposit();
  return useMutation<DepositActionResponse, Error, DepositVars<DepositHoldRequest>>({
    mutationFn: ({ rentalId, itemId, data }) => depositService.holdDeposit(rentalId, itemId, data),
    onSuccess: (_res, { rentalId }) => {
      invalidate(rentalId);
      showSuccess('บันทึกคงมัดจำไว้เรียบร้อยแล้ว');
    },
  });
};

/** ปลดการคงมัดจำ → hold=false (นับวันทำการใหม่) */
export const useUnholdDeposit = () => {
  const invalidate = useInvalidateDeposit();
  return useMutation<DepositActionResponse, Error, DepositVars<DepositUnholdRequest>>({
    mutationFn: ({ rentalId, itemId, data }) => depositService.unholdDeposit(rentalId, itemId, data),
    onSuccess: (_res, { rentalId }) => {
      invalidate(rentalId);
      showSuccess('ปลดการคงมัดจำเรียบร้อยแล้ว — เริ่มนับกำหนดคืนใหม่');
    },
  });
};

/** แก้ไขบันทึกคืน → RETURNED กลับเป็น PENDING_REFUND */
export const useReopenDeposit = () => {
  const invalidate = useInvalidateDeposit();
  return useMutation<DepositActionResponse, Error, DepositVars<DepositReopenRequest>>({
    mutationFn: ({ rentalId, itemId, data }) => depositService.reopenDeposit(rentalId, itemId, data),
    onSuccess: (_res, { rentalId }) => {
      invalidate(rentalId);
      showSuccess('เปิดบันทึกคืนมัดจำใหม่แล้ว — สถานะกลับเป็น "รอคืนมัดจำ"');
    },
  });
};

/** บันทึกรับมัดจำ → PENDING กลับเป็น PAID */
export const useMarkDepositPaid = () => {
  const invalidate = useInvalidateDeposit();
  return useMutation<DepositActionResponse, Error, DepositVars<DepositMarkPaidRequest>>({
    mutationFn: ({ rentalId, itemId, data }) => depositService.markDepositPaid(rentalId, itemId, data),
    onSuccess: (_res, { rentalId }) => {
      invalidate(rentalId);
      showSuccess('บันทึกรับมัดจำเรียบร้อยแล้ว');
    },
  });
};

export default {
  useReturnDeposit,
  useHoldDeposit,
  useUnholdDeposit,
  useReopenDeposit,
  useMarkDepositPaid,
};
